<template>
  <el-card shadow="never" class="admin-card workbench-card">
    <template #header>
      <div class="dashboard-todo-header">
        <div class="workbench-card__title-wrap">
          <span class="admin-dashboard__section-title">{{ t('dashboard.workbenchTitle') }}</span>
          <el-tag v-if="totalCount > 0" type="warning" size="small" class="workbench-card__badge">
            {{ totalCount }}
          </el-tag>
        </div>
      </div>
    </template>

    <div v-loading="loading">
      <el-table
        v-if="items.length > 0"
        :data="items"
        class="workbench-table"
        @row-click="handleRowClick"
      >
        <el-table-column :label="t('dashboard.workbenchType')" width="120">
          <template #default="{ row }">
            <el-tag :type="typeTagMap[row.type] || 'info'" size="small">
              {{ typeLabel(row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="title" :label="t('dashboard.workbenchItem')" min-width="180" show-overflow-tooltip />
        <el-table-column prop="submitter" :label="t('dashboard.workbenchSubmitter')" width="120" show-overflow-tooltip />
        <el-table-column prop="created_at" :label="t('dashboard.workbenchTime')" width="170">
          <template #default="{ row }">
            {{ formatDisplayDateTime(row.created_at) || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="t('dashboard.workbenchAction')" width="200" align="center">
          <template #default="{ row }">
            <template v-if="canQuickAudit(row)">
              <el-button type="success" link :loading="auditingId === rowKey(row)" @click.stop="quickAudit(row, true)">
                {{ t('dashboard.workbenchApprove') }}
              </el-button>
              <el-button type="danger" link :loading="auditingId === rowKey(row)" @click.stop="quickAudit(row, false)">
                {{ t('dashboard.workbenchReject') }}
              </el-button>
            </template>
            <el-button v-else type="primary" link @click.stop="goRoute(row.route)">{{ t('viewMore') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-else class="workbench-empty">
        <hc-empty :image-size="72" :description="t('dashboard.workbenchEmpty')" />
      </div>

      <div v-if="summaryChips.length" class="workbench-summary">
        <div
          v-for="chip in summaryChips"
          :key="chip.type"
          class="workbench-chip"
          @click="goRoute(chip.route)"
        >
          <span class="workbench-chip__label">{{ chip.label }}</span>
          <span class="workbench-chip__count">{{ chip.count }}</span>
        </div>
      </div>
    </div>
  </el-card>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import hcEmpty from '@/components/hc-empty/index.vue'
import { getWorkbenchApi } from '@/api/home/workbench.js'
import { usePageI18n } from '@/composables/usePageI18n.js'
import { isPathAllowedForUser } from '@/utils/adminMenuPermission.js'
import { auditWorkbenchItem, canQuickAudit } from '@/utils/workbenchAudit.js'
import { formatDisplayDateTime } from '@/utils/dateTime.js'

const router = useRouter()
const loading = ref(true)
const auditingId = ref('')
const items = ref([])
const counts = ref({})
const totalCount = ref(0)

const typeTagMap = {
  maintenance: 'warning',
  special: 'primary',
  improve: 'success',
  anomaly: 'danger',
  overtime: 'info',
}

const typeLabelMap = ref({})

const applyLabels = () => {
  typeLabelMap.value = {
    maintenance: t('dashboard.workbenchMaintenance'),
    special: t('dashboard.workbenchSpecial'),
    improve: t('dashboard.workbenchImprove'),
    anomaly: t('dashboard.workbenchAnomaly'),
    overtime: t('dashboard.workbenchOvertime'),
  }
}
const { t } = usePageI18n(applyLabels)

const routeMap = {
  maintenance: '/equipment/maintenance',
  special: '/task/submitRecord',
  improve: '/improve/submitRecord',
  anomaly: '/attendance/anomaly',
  overtime: '/attendance/overtime',
}

const summaryChips = computed(() => {
  return Object.keys(routeMap)
    .filter((type) => isPathAllowedForUser(routeMap[type]))
    .map((type) => ({
      type,
      route: routeMap[type],
      label: typeLabelMap.value[type] || type,
      count: counts.value[type] ?? 0,
    }))
    .filter((chip) => chip.count > 0)
})

const typeLabel = (type) => typeLabelMap.value[type] || type

const loadWorkbench = () => {
  loading.value = true
  getWorkbenchApi('{}')
    .then((res) => {
      const data = res?.data || {}
      items.value = (data.items || []).filter((item) => isPathAllowedForUser(item.route))
      counts.value = data.counts || {}
      totalCount.value = data.total ?? 0
    })
    .finally(() => {
      loading.value = false
    })
}

const goRoute = (path) => {
  if (path && isPathAllowedForUser(path)) {
    router.push(path)
  }
}

const handleRowClick = (row) => {
  if (canQuickAudit(row)) {
    return
  }
  goRoute(row.route)
}

const rowKey = (row) => `${row.type}-${row.id}`

const quickAudit = async (row, approved) => {
  const actionLabel = approved ? t('dashboard.workbenchApprove') : t('dashboard.workbenchReject')
  try {
    await ElMessageBox.confirm(
      `${actionLabel}：${row.title || ''}`,
      t('dashboard.workbenchConfirmTitle'),
      { type: approved ? 'success' : 'warning', confirmButtonText: t('common.confirm'), cancelButtonText: t('common.cancel') }
    )
  } catch {
    return
  }

  auditingId.value = rowKey(row)
  try {
    const res = await auditWorkbenchItem(row, approved)
    if (res?.code === 200) {
      ElMessage.success(t('improveDetail.opSuccess'))
      loadWorkbench()
    } else {
      ElMessage.error(res?.msg || t('improveDetail.opFailed'))
    }
  } catch {
    ElMessage.error(t('improveDetail.opFailed'))
  } finally {
    auditingId.value = ''
  }
}

onMounted(() => {
  applyLabels()
  loadWorkbench()
})
</script>

<style lang="less" scoped>
.workbench-card__title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.workbench-card__badge {
  font-weight: 600;
}

.workbench-table {
  cursor: pointer;
}

.workbench-empty {
  padding: 12px 0 4px;
}

.workbench-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--admin-card-border);
}

.workbench-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: var(--admin-radius-md);
  background: #f8fafc;
  border: 1px solid #eef2f6;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #f1f5f9;
    border-color: #e2e8f0;
  }
}

.workbench-chip__label {
  font-size: 13px;
  color: #475569;
}

.workbench-chip__count {
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--admin-primary);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
